import { getSupabaseClient } from '../utils/supabase/client';
import { WORKSPACE_STAGES, WS_SOURCE_LABELS, type WorkspaceDeal, type WorkspaceDealSource, type WorkspaceStage } from '../features/pipeline/types';

const BATCH_SIZE = 200;
const FIELDS = 'id,workspace_id,title,client_name,value,stage,source,planning_ref,contact_id,assigned_to,notes,tender_deadline,lost_reason,won_at,lost_at,created_at,updated_at';

function isWorkspaceStage(value: string): value is WorkspaceStage {
  return WORKSPACE_STAGES.some(stage => stage.id === value);
}
function isWorkspaceSource(value: string): value is WorkspaceDealSource {
  return Object.prototype.hasOwnProperty.call(WS_SOURCE_LABELS, value);
}

/** Caller-authenticated reads only. Enquiries retain their own stored stages. */
export async function fetchWorkspaceEnquiries(workspaceId: string, options: { signal?: AbortSignal } = {}): Promise<WorkspaceDeal[]> {
  if (!workspaceId) throw new Error('Workspace is required');
  const client = getSupabaseClient();
  const deals: WorkspaceDeal[] = [];
  for (let offset = 0; ; offset += BATCH_SIZE) {
    options.signal?.throwIfAborted();
    let query = client.from('workspace_deals').select(FIELDS)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false }).order('id', { ascending: false })
      .range(offset, offset + BATCH_SIZE - 1);
    if (options.signal) query = query.abortSignal(options.signal);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    options.signal?.throwIfAborted();
    for (const row of data ?? []) {
      if (row.workspace_id !== workspaceId) throw new Error('Enquiry workspace does not match the active workspace');
      if (!isWorkspaceStage(row.stage) || (row.source !== null && !isWorkspaceSource(row.source))) {
        throw new Error('Enquiry has an unsupported stage or source');
      }
      deals.push({ ...row, stage: row.stage, source: row.source });
    }
    if ((data?.length ?? 0) < BATCH_SIZE) return deals;
  }
}
